import {
  ChatMessageAttachmentType,
  InstagramMessageType,
  ParsedApiError,
  SendInstagramMessageMutationParams,
  apiErrorHasCode,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import {
  ChatInput,
  ChatInputHandle,
  ChatInputParams,
} from "@web-src/modules/chats/components/ChatInput";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useRef, useState, ReactNode } from "react";
import { useTranslations } from "@jugl-web/utils";
import { useClientSupportPageWithSelectedConversation } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageWithSelectedConversation";
import { useInstagramAccount } from "../../../../providers/InstagramAccountProvider/useInstagramAccount";
import { DisconnectedChatInput } from "../DisconnectedChatInput";
import { CRMInvalidUserPopup } from "../CRMInvalidUserPopup";

const MAX_TEXT_LENGTH = 1000;

export const InstagramChatInput: FC<{
  chatId: string;
  onMessageSent?: () => void;
}> = ({ chatId, onMessageSent }) => {
  const { integrationsApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();
  const { t } = useTranslations();
  const {
    selectedConversation,
    incomingInstagramMessages$,
    setIsSendingMessage,
    isChatExpired,
  } = useClientSupportPageWithSelectedConversation();
  const { isConnected, loginInstagramAccount } = useInstagramAccount();

  const [sendInstagramMessage, { isLoading: isSendingInstagramMessage }] =
    integrationsApi.useSendInstagramMessageMutation();
  const [isInvalidUserPopupOpen, setIsInvalidUserPopupOpen] = useState(false);
  const [uploadInstagramMedia, { isLoading: isUploadingInstagramMedia }] =
    integrationsApi.useUploadInstagramMediaMutation();
  const chatInput$ = useRef<ChatInputHandle>(null);

  const isSendingMessage =
    isSendingInstagramMessage || isUploadingInstagramMedia;

  const handleSendMessage: ChatInputParams["onSubmit"] = async ({
    body,
    attachments,
  }) => {
    const sendMessageMeta = {
      onError: (error: ParsedApiError) => {
        if (apiErrorHasCode(error, "invalid_user_id")) {
          setIsInvalidUserPopupOpen(true);
          return undefined;
        }
        return "unhandled" as const;
      },
    };

    try {
      setIsSendingMessage(true);

      const attachmentTypeMap: Partial<
        Record<ChatMessageAttachmentType, Exclude<InstagramMessageType, "text">>
      > = {
        [ChatMessageAttachmentType.image]: InstagramMessageType.image,
        [ChatMessageAttachmentType.audio]: InstagramMessageType.audio,
        [ChatMessageAttachmentType.voice]: InstagramMessageType.audio,
        [ChatMessageAttachmentType.video]: InstagramMessageType.video,
      };

      const { audioAttachments, otherAttachments } = attachments?.reduce<{
        audioAttachments: typeof attachments;
        otherAttachments: typeof attachments;
      }>(
        (acc, att) => {
          if (
            att.type === ChatMessageAttachmentType.audio ||
            att.type === ChatMessageAttachmentType.voice
          ) {
            acc.audioAttachments.push(att);
          } else {
            acc.otherAttachments.push(att);
          }
          return acc;
        },
        { audioAttachments: [], otherAttachments: [] }
      ) ?? { audioAttachments: [], otherAttachments: [] };

      if (audioAttachments.length) {
        await Promise.all(
          audioAttachments.map(async (att) => {
            const type = attachmentTypeMap[att.type];
            if (!type) return;

            const { media_id: mediaId, url } = await uploadInstagramMedia({
              entityId,
              file: att.file,
            }).unwrap();

            const payload: SendInstagramMessageMutationParams["data"] = {
              payload: {
                timestamp: Date.now(),
                recipient: { id: selectedConversation.conv_id },
                message: {
                  attachments: [{ media_id: mediaId, type, payload: { url } }],
                },
              },
              msg_type: "attachment",
            };

            const response = await sendInstagramMessage({
              entityId,
              data: payload,
              meta: sendMessageMeta,
            }).unwrap();

            incomingInstagramMessages$.next(response);
          })
        );
      }

      if (otherAttachments.length) {
        const uploadedOther = await Promise.all(
          otherAttachments.map(async (att) => {
            const type = attachmentTypeMap[att.type];
            if (!type) return null;

            const { media_id: mediaId, url } = await uploadInstagramMedia({
              entityId,
              file: att.file,
            }).unwrap();

            return { media_id: mediaId, type, payload: { url } };
          })
        );

        const filteredOther = uploadedOther.filter(Boolean) as NonNullable<
          (typeof uploadedOther)[number]
        >[];

        if (filteredOther.length > 0) {
          const payload: SendInstagramMessageMutationParams["data"] = {
            payload: {
              timestamp: Date.now(),
              recipient: { id: selectedConversation.conv_id },
              message: { attachments: filteredOther },
            },
            msg_type: "attachment",
          };

          const response = await sendInstagramMessage({
            entityId,
            data: payload,
            meta: sendMessageMeta,
          }).unwrap();

          incomingInstagramMessages$.next(response);
        }
      }

      const bodyText = body?.trim() || "";
      if (bodyText) {
        const textPayload: SendInstagramMessageMutationParams["data"] = {
          payload: {
            timestamp: Date.now(),
            recipient: { id: selectedConversation.conv_id },
            message: { text: bodyText },
          },
        };

        const textResponse = await sendInstagramMessage({
          entityId,
          data: textPayload,
          meta: sendMessageMeta,
        }).unwrap();

        incomingInstagramMessages$.next(textResponse);
      }

      if ((attachments?.length ?? 0) > 0 || bodyText) {
        onMessageSent?.();
        chatInput$.current?.clear();
        chatInput$.current?.focus();
      }
    } catch {
      // Error UI handled by apiResultMiddleware via meta / default toast
    } finally {
      setIsSendingMessage(false);
    }
  };

  if (!isConnected) {
    return (
      <DisconnectedChatInput
        title={t({
          id: "client-support-page.account-is-disconnected",
          defaultMessage: "Account is Disconnected",
        })}
        description={t(
          {
            id: "client-support-page.please-reconnect-to-continue-using-instagram-inbox",
            defaultMessage:
              "Please <highlightedText>reconnect</highlightedText> to continue using Instagram Inbox",
          },
          {
            highlightedText: (text: ReactNode) => (
              <span className="font-medium text-primary">{text}</span>
            ),
          }
        )}
        buttonProps={{
          text: t({
            id: "common.reconnect",
            defaultMessage: "Reconnect",
          }),
          onClick: loginInstagramAccount,
        }}
      />
    );
  }

  if (isChatExpired) {
    return (
      <DisconnectedChatInput
        variant="expired"
        title={t({
          id: "client-support-page.chat-is-expired",
          defaultMessage: "Chat is Expired",
        })}
        description={t(
          {
            id: "client-support-page.chat-is-expired-description",
            defaultMessage:
              "Conversations are automatically marked as expired after 24 hours of customer inactivity to keep your inbox <highlightedText>focused on active visitors</highlightedText>",
          },
          {
            highlightedText: (text: ReactNode) => (
              <span className="bg-linear-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                {text}
              </span>
            ),
          }
        )}
      />
    );
  }

  return (
    <>
      <div className="border-0 border-t border-solid border-dark-100 px-8 py-6">
        <ChatInput
          variant="inbox"
          id={chatId}
          ref={chatInput$}
          maxTextLenght={MAX_TEXT_LENGTH}
          onSubmit={handleSendMessage}
          isLoading={isSendingMessage}
          toolbarActions={{
            emoji: {
              enabled: true,
            },
            attachments: {
              enabled: !isSendingMessage,
              isCaptionHidden: true,
              isMultiple: true,
              accept: {
                audio: ".m4a,.wav",
                image: ".jpg,.jpeg,.png",
                video: ".mp4,.ogg,.avi,.mov,.webm",
              },
              maxSize: {
                audio: 25,
                image: 8,
                video: 25,
              },
            },
          }}
        />
      </div>
      <CRMInvalidUserPopup
        isOpen={isInvalidUserPopupOpen}
        onRequestClose={() => setIsInvalidUserPopupOpen(false)}
      />
    </>
  );
};
